import { Link, useRouter } from 'found';
import React, { useState } from 'react';
import { commitMutation, graphql, fetchQuery } from 'react-relay';
import styled from 'styled-components';
import { setCurrentAccount, getCurrentAccount } from '../../account_helpers';
import environment from '../../api/environment';
import Button from '../../components/input/Button';
import FlatButton from '../../components/input/FlatButton';
import TextInput from '../../components/input/TextInput';
import Spacer from '../../components/Spacer';
import { HeadingNoMargin } from '../../components/typography/Typography';
import { routes } from '../../routes';
import { useToastDispatch } from '../../components/Toast/ToastProvider';
import { ScaleLoader } from 'react-spinners';
import LogoGradientIcon from '../../components/LogoGradientIcon';
import { isSameOriginRedirect } from '../../helpers';

const Container = styled.div`
  width: 90vw;
  @media (min-width: 1200px) {
    width: 341px;
  }
`;

const ForgotText = styled.a`
  font-size: 12px;
  color: #9092aa;
  margin-left: 7px;
  &:hover {
    color: #414250;
  }
  text-decoration: none;
`;

const LogoButton = styled.a`
  padding: 10px;
  display: inline-flex;
  border-radius: 5px;
  transition: all 0.2s;
  &:hover {
    background: #f4f5ff;
  }
`;

const mutation = graphql`
  mutation Login_LoginMutation($email: String!, $password: String!) {
    login(input: { email: $email, password: $password }) {
      id
    }
  }
`;

export const attemptLogin = async (email: string, password: string) => {
  const variables = {
    email,
    password,
  };

  await new Promise((resolve, reject) => {
    //@ts-ignore
    commitMutation(environment, {
      mutation,
      variables,
      onCompleted: async (response: any, errors: unknown) => {
        if (errors) {
          // Display error
          // errorCallback(`${errors[0]?.extensions?.message}`);
          reject(errors);
          return;
        }

        resolve(response);
      },
      onError: (err: any) => {
        reject(err);
      },
    });
  });
};

function Login() {
  document.title = 'Log In - PayPI';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const dispatch = useToastDispatch();
  const { router, match } = useRouter();

  const login = async () => {
    try {
      setLoading(true);
      const resp = await attemptLogin(email, password);
      console.log('Success', resp);

      const data = await fetchQuery<any>(
        //@ts-ignore
        environment,
        graphql`
          query Login_AccountQuery {
            viewer {
              userAccounts {
                id
              }
            }
          }
        `,
        {}
        //@ts-ignore
      ).toPromise();

      //

      if (data.viewer.userAccounts[0].id) {
        if (!getCurrentAccount()) {
          setCurrentAccount(data.viewer.userAccounts[0].id);
        }

        if (
          getCurrentAccount() &&
          !data.viewer.userAccounts.includes(getCurrentAccount())
        ) {
          setCurrentAccount(data.viewer.userAccounts[0].id);
        }
      } else {
        dispatch({
          alert: 'error',
          text: 'Unable to select account, please contact PayPI Support',
        });
        return;
      }

      const searchParams = new URLSearchParams(match.location.search);
      const redirect = searchParams.get('redirect');

      if (!redirect) {
        return router.push(routes.subscriptions);
      }

      if (isSameOriginRedirect(redirect, window.location.href)) {
        dispatch({
          alert: 'error',
          text: 'Dangerous redirect detected, stopping.',
        });
        return;
      }

      return router.push(redirect);
    } catch (err) {
      setLoading(false);
      dispatch({
        alert: 'error',
        text: 'Unable to login, please try again',
      });
      console.warn('Err', err);
    }
  };

  const handleKey = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      login();
    }
  };

  return (
    <Container>
      <LogoButton href={'https://paypi.dev'}>
        <LogoGradientIcon />
      </LogoButton>
      <Spacer vertical spacing={2} />
      <HeadingNoMargin>Login</HeadingNoMargin>
      <Spacer vertical spacing={3} />
      <form
        onSubmit={(evt) => {
          evt.preventDefault();
          login();
        }}
      >
        <TextInput
          label='EMAIL'
          fullWidth
          type={'email'}
          value={email}
          onKeyDown={(e) => handleKey(e)}
          placeholder='email@email.com'
          onChange={setEmail}
        />
        <Spacer vertical spacing={2} />
        <TextInput
          label='PASSWORD'
          fullWidth
          type={'password'}
          value={password}
          onKeyDown={(e) => handleKey(e)}
          onChange={setPassword}
          rightExtraInfo={
            <Link to={routes.forgotPassword} tabIndex={3} as={ForgotText}>
              Forgotten your password?
            </Link>
          }
        />
        <Spacer vertical spacing={3} />

        <Button color='white' onClick={() => login()}>
          {!loading && 'Login'}
          {loading && <ScaleLoader color={'#e4e4e6'} height={11} />}
        </Button>
        <input type='submit' hidden />
      </form>
      <Spacer vertical spacing={2} />
      <FlatButton
        darkenOnHover
        color='#5c6ef8'
        onClick={() => router.push(routes.customerSignup)}
      >
        I don't have an account
      </FlatButton>
    </Container>
  );
}

export default Login;
